/**
 * Google Maps Service
 *
 * Dynamically loads the Google Maps JS API and provides helpers
 * for geocoding and creating map markers.
 *
 * Usage:
 *   import { loadGoogleMapsApi, geocodeGoogle, createMarker, createInfoWindow } from './googleMapsService';
 *
 *   await loadGoogleMapsApi('YOUR_API_KEY');
 *   const coords = await geocodeGoogle('Some address');
 */

let googleApiPromise = null;
let googleApiLoaded = false;

/**
 * Load the Google Maps JavaScript API dynamically.
 * Returns a promise that resolves when the API is ready.
 * Caches the promise so it's only loaded once.
 * Times out after LOAD_TIMEOUT_MS if the callback never fires (e.g. blocked by adblocker or invalid key).
 *
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise<typeof google>}
 */
export function loadGoogleMapsApi(apiKey) {
  const LOAD_TIMEOUT_MS = 10000; // 10 seconds

  if (googleApiLoaded && window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (googleApiPromise) {
    return googleApiPromise;
  }

  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  googleApiPromise = new Promise((resolve, reject) => {
    // Check if already loaded by another script
    if (window.google && window.google.maps) {
      googleApiLoaded = true;
      resolve(window.google.maps);
      return;
    }

    // Create a global callback function name with a unique suffix to avoid collisions
    const callbackName = `__googleMapsCallback_${Date.now()}`;

    // Listen for Google Maps auth failure event (triggered when API key is invalid/blocked)
    const authFailureHandler = () => {
      cleanup();
      reject(new Error('Google Maps API authentication failed'));
    };
    window.addEventListener('gm_authFailure', authFailureHandler);

    // Define the callback function on window
    window[callbackName] = () => {
      cleanup();
      // Check if google maps actually loaded (it might not if key is restricted)
      if (window.google && window.google.maps) {
        googleApiLoaded = true;
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps API failed to initialize'));
      }
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      window.removeEventListener('gm_authFailure', authFailureHandler);
    };

    // Timeout to prevent hanging if the callback never fires
    const timeoutId = setTimeout(() => {
      cleanup();
      googleApiPromise = null;
      reject(new Error('Google Maps API load timed out (check adblocker or API key restrictions)'));
    }, LOAD_TIMEOUT_MS);

    // Create script element with loading=async for optimal performance
    // Note: No libraries=places to avoid "Permission Denied" if Places API is not enabled.
    // The GoogleMapView component only uses core Maps API (Markers, InfoWindow, Circle).
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      cleanup();
      googleApiPromise = null;
      reject(new Error('Failed to load Google Maps API script (check adblocker or API key restrictions)'));
    };

    document.head.appendChild(script);
  });

  return googleApiPromise;
}

/**
 * Geocode an address using the Google Maps Geocoding service.
 *
 * @param {string} address - The address to geocode
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export async function geocodeGoogle(address) {
  try {
    const maps = await googleApiPromise;
    if (!maps) return null;

    return new Promise((resolve) => {
      const geocoder = new maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          resolve(null);
        }
      });
    });
  } catch (err) {
    console.warn('[GoogleMapsService] Geocode error:', err.message);
    return null;
  }
}

/**
 * Concurrency limiter for parallel operations.
 * Ensures at most N operations run simultaneously.
 */
export class ConcurrencyLimiter {
  constructor(maxConcurrent) {
    this.maxConcurrent = maxConcurrent;
    this.active = 0;
    this.queue = [];
  }

  async run(fn) {
    if (this.active >= this.maxConcurrent) {
      await new Promise(resolve => this.queue.push(resolve));
    }
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next();
      }
    }
  }
}

/**
 * Check if Google Maps API is loaded.
 */
export function isGoogleMapsLoaded() {
  return googleApiLoaded || !!(window.google && window.google.maps);
}

/**
 * Reset the Google Maps API promise (useful for testing or re-loading).
 */
export function resetGoogleMapsApi() {
  googleApiPromise = null;
  googleApiLoaded = false;
}